﻿import { useMemo, useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArchiveRestore, PackageCheck, PackageX, Download } from "lucide-react";
import { toast } from "react-toastify";

import useListController from "@/hooks/list/useListController";
import { useScreenPermissions } from "@/hooks/general/useScreenPermissions";
import { useHistoriesLookups, useHistories } from "@/hooks/histories/useHistories";
import { useIOReturn } from "@/hooks/histories/useIOReturn";
import { useHistoryExport } from "@/hooks/histories/useHistoryExport";
import { useInUseHistoryFilters } from "./hooks/useInUseHistoryFilters";
import { useInUseHistoryColumns } from "./hooks/useInUseHistoryColumns";
import { useInUseHistoryActions } from "./hooks/useInUseHistoryActions";

import useFilterStore from "@/stores/useFilterStore";
import { SCREEN_ID_PRODUCTS_OUTSIDE_WAREHOUSE } from "@/constants/screenIds";

import CustomFetching from "@/components/desktop/fetching/CustomFetching";
import ListScaffold from "@/components/desktop/list/table/ListScaffold";
import CustomSelectActions from "@/components/desktop/list/select-actions/CustomSelectActions";
import CustomConfirmModal from "@/components/desktop/modal/CustomConfirmModal";
import CustomProcessingModal from "@/components/desktop/modal/CustomProcessingModal";
import CustomNoPermissionView from "@/components/desktop/permission/no-permission/CustomNoPermissionView";

import IOForm from "@/pages/Histories/IO/IOForm/IOForm";

import { IN_USE_FILTERS, IN_USE_METADATA, IN_USE_BASE_TABLE } from "./in-use-history.constants";
import { getHistoryQuickActions } from "../Home/histories-metadata";

const IN_USE_ICON_MAP = { ArchiveRestore, PackageCheck, PackageX };

const RETURN_ACTION_META = {
	empty: {
		confirmTitle: "¿Devolver envases vacíos?",
		confirmMessage: (count) => `Se registrará la devolución de ${count} envases como vacíos. Esta acción no se puede deshacer.`,
		processingMessage: "Registrando devolución de envases vacíos...",
	},
	not_used: {
		confirmTitle: "¿Registrar como no utilizados?",
		confirmMessage: (count) => `Se marcarán ${count} envases como no utilizados. Esta acción no se puede deshacer.`,
		processingMessage: "Registrando envases no utilizados...",
	},
	receive: {
		confirmTitle: "¿Recepcionar traspasos?",
		confirmMessage: (count) => `Se registrará la recepción de ${count} traspaso(s). Esta acción no se puede deshacer.`,
		processingMessage: "Registrando recepción de traspasos...",
	},
};

const InUseHistory = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { filter } = useFilterStore();
	const { estate_parent_id, estate_id, search, parameter } = filter;
	const { canShow, canEdit, canDelete, isLoading: isLoadingPermissions } = useScreenPermissions(SCREEN_ID_PRODUCTS_OUTSIDE_WAREHOUSE);
	const canLoadScreenData = !isLoadingPermissions && canShow;

	const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
	const action = searchParams.get("action");
	const historyIdParam = searchParams.get("id");

	const isModalOpen = useMemo(() => {
		return canEdit && action === "editar" && historyIdParam !== null && historyIdParam !== "";
	}, [action, canEdit, historyIdParam]);

	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [selectedHistoryId, setSelectedHistoryId] = useState(null);

	const [selectedRows, setSelectedRows] = useState({});
	const [pendingReturnState, setPendingReturnState] = useState(null);

	const {
		isFilterOpen,
		filtersState,
		appliedFiltersState,
		tableState,
		appliedFilterParams,
		reactTableSort,
		openFilter,
		closeFilter,
		handleFilterChange,
		handleApplyFilters,
		handleClearAllFilters,
		handleClearOneFilter,
		handlePageChange,
		handlePageSizeChange,
		handleSortByChange,
		setFilterOptions,
	} = useListController({
		baseFilter: IN_USE_FILTERS,
		baseTable: IN_USE_BASE_TABLE,
	});

	const { data: lookups, isLoading: isLoadingLookups } = useHistoriesLookups(
		{
			type: "in_use",
			estate_id,
			estate_parent_id,
			search,
			parameter,
		},
		{
			enabled: canLoadScreenData,
		},
	);

	const { isReady } = useInUseHistoryFilters({
		lookups,
		filtersState,
		setFilterOptions,
		handleFilterChange,
		handleApplyFilters,
	});

	const filters = useMemo(
		() => ({
			...appliedFilterParams,
			estate_id,
			estate_parent_id,
			search,
			parameter,
			...tableState,
		}),
		[appliedFilterParams, estate_id, estate_parent_id, search, parameter, tableState],
	);

	const { data, isLoading, isFetching } = useHistories(IN_USE_METADATA.apiType, filters, {
		enabled: canLoadScreenData && !isLoadingLookups && Boolean(lookups) && isReady,
	});

	const currentPageRows = data?.data || [];

	const handleRowSelect = (rowIndex, rowData, checked) => {
		setSelectedRows((prev) => {
			if (checked) return { ...prev, [rowIndex]: { ...rowData } };
			const next = { ...prev };
			delete next[rowIndex];
			return next;
		});
	};

	const handleSelectAll = (checked) => {
		if (!checked) return setSelectedRows({});
		const next = {};
		currentPageRows.forEach((rowData, index) => {
			next[String(index)] = { ...rowData };
		});
		setSelectedRows(next);
	};

	useEffect(() => {
		setSelectedRows({});
	}, [data]);

	const selectionState = {
		selectedRows,
		onRowSelect: handleRowSelect,
		onSelectAll: handleSelectAll,
		allRowsCount: currentPageRows.length,
	};

	const { handleEdit, handleDelete, isDeleting } = useInUseHistoryActions(IN_USE_METADATA.apiType, filters);

	const columns = useInUseHistoryColumns(tableState, selectionState, {
		onEdit: canEdit ? handleEdit : null,
		onDelete: canDelete
			? (applicationId) => {
					setSelectedHistoryId(applicationId);
					setShowDeleteModal(true);
				}
			: null,
	});

	const confirmDelete = async () => {
		if (!selectedHistoryId) return;
		await handleDelete(selectedHistoryId);
		setShowDeleteModal(false);
		setSelectedHistoryId(null);
	};

	const quickActions = useMemo(() => {
		const actions = getHistoryQuickActions("en_uso");
		return actions.map((action) => ({
			...action,
			onClick: () => navigate(action.path),
		}));
	}, [navigate]);

	const { handleExport, isAlreadyExporting } = useHistoryExport("in_use", appliedFilterParams);

	const exportButton = useMemo(
		() => [
			{
				key: "export",
				label: "Exportar",
				icon: Download,
				variant: "secondary",
				disabled: isAlreadyExporting,
				options: [
					{ key: "excel", label: "Excel General", onClick: () => handleExport("nuevo_excel") },
					{ key: "excel_detail", label: "Excel Detalle", onClick: () => handleExport("nuevo_excel_detalle") },
				],
			},
		],
		[handleExport, isAlreadyExporting],
	);

	const selectedCount = Object.keys(selectedRows).length;

	const { mutate: returnIOAction, isLoading: isReturning } = useIOReturn(IN_USE_METADATA.apiType, filters);

	const handleReturnAction = useCallback(
		(action, rowFilter) => {
			const ioIds = Object.values(selectedRows)
				.filter((row) => rowFilter(row))
				.flatMap((row) => row.io_ids || []);

			if (ioIds.length === 0) {
				toast.warning("No hay envases recuperables para la selección actual.");
				return;
			}

			setPendingReturnState({ ioIds, action });
		},
		[selectedRows],
	);

	const handleConfirmReturn = useCallback(() => {
		if (!pendingReturnState) return;
		const { ioIds, action } = pendingReturnState;
		returnIOAction(
			{ ioIds, action },
			{
				onSuccess: (response) => {
					const errorCount = response?.errors?.length || 0;
					const processedCount = response?.processed || 0;
					if (errorCount > 0) {
						toast.warning(`${processedCount} envases procesados, ${errorCount} con errores.`);
					} else {
						toast.success(`${processedCount} envases procesados correctamente.`);
					}
					setSelectedRows({});
					setPendingReturnState(null);
				},
				onError: () => {
					toast.error("Error al procesar la solicitud.");
					setPendingReturnState(null);
				},
			},
		);
	}, [pendingReturnState, returnIOAction]);

	const dynamicActions = useMemo(() => {
		if (!canEdit) return [];
		const list = Object.values(selectedRows);
		const otherCount = list.filter((r) => r.type_movement !== 2).length;
		const transferCount = list.filter((r) => r.type_movement === 2).length;
		return [
			otherCount > 0 && {
				label: `Devolver vacíos los ${otherCount} envases seleccionados`,
				icon: "ArchiveRestore",
				variant: "primary",
				onClick: () => handleReturnAction("empty", (r) => r.type_movement !== 2),
			},
			otherCount > 0 && {
				label: `No utilizados los ${otherCount} envases seleccionados`,
				icon: "PackageX",
				variant: "warning",
				onClick: () => handleReturnAction("not_used", (r) => r.type_movement !== 2),
			},
			transferCount > 0 && {
				label: `Recepcionar los ${transferCount} traspasos seleccionados`,
				icon: "PackageCheck",
				variant: "info",
				onClick: () => handleReturnAction("receive", (r) => r.type_movement === 2),
			},
		].filter(Boolean);
	}, [canEdit, selectedRows, handleReturnAction]);

	if (isLoadingPermissions) {
		return <CustomFetching center message="Validando permisos..." />;
	}

	if (!canShow) {
		return <CustomNoPermissionView />;
	}

	return (
		<>
			<ListScaffold
				quickActions={quickActions}
				actionButtons={exportButton}
				subtitle={IN_USE_METADATA.subtitle}
				filterTitle={`Filtrar ${IN_USE_METADATA.title}`}
				isFilterOpen={isFilterOpen}
				filters={filtersState}
				appliedFilters={appliedFiltersState}
				onFilterChange={handleFilterChange}
				onApplyFilters={handleApplyFilters}
				onClearFilters={handleClearAllFilters}
				onClearOneFilter={handleClearOneFilter}
				onOpenFilter={openFilter}
				onCloseFilter={closeFilter}
				isFetching={isFetching}
				isLoading={isLoading || isLoadingLookups}
				data={currentPageRows}
				totalItems={data?.totalRecords || 0}
				totalPages={data?.totalPages || 0}
				toItem={currentPageRows.length || 0}
				fromItem={data?.page === 1 ? 1 : (data?.page - 1) * tableState.per_page + 1}
				topContent={canEdit && selectedCount > 0 && <CustomSelectActions iconMap={IN_USE_ICON_MAP} actions={dynamicActions} disabled={isReturning} />}
				pageIndex={tableState.page}
				columns={columns}
				onPageChange={handlePageChange}
				pageSize={tableState.per_page}
				onPageSizeChange={handlePageSizeChange}
				sortBy={reactTableSort}
				onSortByChange={handleSortByChange}
				pageSizeOptions={[20, 50, 100, 200]}
				initialPageSize={tableState.per_page}
				initialSortBy={reactTableSort}
				compactHeaders={true}
				compactBody={true}
			/>
			{pendingReturnState && (
				<CustomConfirmModal
					open={true}
					onClose={() => !isReturning && setPendingReturnState(null)}
					onConfirm={handleConfirmReturn}
					title={RETURN_ACTION_META[pendingReturnState.action].confirmTitle}
					message={RETURN_ACTION_META[pendingReturnState.action].confirmMessage(pendingReturnState.ioIds.length)}
					confirmText="Sí, confirmar"
					cancelText="No, cancelar"
					isLoading={isReturning}
					processingTitle="Procesando operación"
					processingMessage={RETURN_ACTION_META[pendingReturnState.action].processingMessage}
				/>
			)}
			<CustomConfirmModal
				open={showDeleteModal}
				onClose={() => setShowDeleteModal(false)}
				onConfirm={confirmDelete}
				title="Eliminar aplicación"
				message={
					<div>
						¿Está seguro que desea eliminar esta aplicación?
						<br />
						<br />
						Si elimina la aplicación seleccionada, no podrá recuperarla.
						<br />
						1. Si es una aplicación de salida, se eliminará su entrada asociada.
						<br />
						2. Si es una aplicación de traspaso, se eliminará el stock cargado en el campo de destino.
					</div>
				}
				confirmText="Sí, eliminar"
				cancelText="No, cancelar"
				isLoading={isDeleting}
			/>
			<CustomProcessingModal open={isDeleting} message="Eliminando aplicación de IO..." />
			<IOForm
				isOpen={isModalOpen}
				historyId={historyIdParam}
				listType={IN_USE_METADATA.apiType}
				filters={{
					...appliedFilterParams,
					estate_id,
					estate_parent_id,
					search,
					parameter,
					...tableState,
				}}
				onClose={() => navigate("/historiales/en_uso")}
			/>
		</>
	);
};

export default InUseHistory;
